import Logo from '../../../assets/small_logo.png'
import homeNavData from './data'
import internshipNavData from './internshipNavData'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { HiOutlineMenu, HiOutlineX } from 'react-icons/hi'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import './navbar.css'

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [courses, setCourses] = useState([]);

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  // Determine if we're on an internship page
  const isInternshipPage = location.pathname.startsWith('/virtual-internship/');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await fetch(`${API_URL}/api/courses`);
        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data)) {
            setCourses(json.data);
          }
        }
      } catch (err) {
        console.error("Failed to fetch courses for navbar:", err);
      }
    };
    fetchCourses();
  }, [API_URL]);
  
  // Use appropriate nav data based on page type
  const baseData = isInternshipPage ? internshipNavData : homeNavData;

  // Dynamically map menu data to inject database courses into any submenu
  const data = baseData.map(item => {
    if (item.submenu) {
      const dynamicSub = courses.length > 0
        ? courses.map((c, index) => ({
            id: 2000 + index,
            link: `/virtual-internship/${c.slug}`,
            title: c.shortTitle || c.title
          }))
        : item.submenu;
      return { ...item, submenu: dynamicSub };
    }
    return item;
  });

  const isLinkActive = (link) => {
    if (!link) return false;
    // internal anchor on home page
    if (link.startsWith('/#')) {
      return location.pathname === '/' && location.hash === `#${link.substring(2)}`;
    }
    // page anchors
    if (link.startsWith('#')) {
      return location.hash === `#${link.substring(1)}`;
    }
    // root
    if (link === '/') return location.pathname === '/';
    // route startsWith match for internship pages
    try {
      return location.pathname === link || location.pathname.startsWith(link + '/') || location.pathname.startsWith(link);
    } catch (e) {
      return false;
    }
  };

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setActiveDropdown(null);
    setMenuOpen(false);

    if (link === '/') {
      navigate('/');
    } else if (link.startsWith('/#')) {
      const anchor = link.substring(2);

      if (location.pathname === '/') {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } else if (link.startsWith('#')) {
      // Handle internal page anchors (for internship pages)
      const anchor = link.substring(1);
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(link);
    }
  };

  const toggleDropdown = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  const toggleMenu = () => {
    setMenuOpen((current) => !current);
    setActiveDropdown(null);
  };

  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname, location.hash]);

  return (
    <nav className={isInternshipPage ? 'nav-internship' : ''}>
      <div className="container nav__container">
        <button
          type="button"
          className="nav__toggle"
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
        >
          {menuOpen ? <HiOutlineX /> : <HiOutlineMenu />}
        </button>
        <a href="/" onClick={(e) => handleNavClick(e, '/')} className='nav__logo'>
          <img src={Logo} alt="Logo" />
        </a>
        <ul
          id="primary-navigation"
          className={`nav__menu ${menuOpen ? 'nav__menu--open' : ''}`}
        >
          {
            data.map(item => {
              const hasSub = !!item.submenu;
              const parentActive = hasSub && item.submenu.some((s) => isLinkActive(s.link));
              return (
                <li
                  key={item.id}
                  className={hasSub ? 'nav__item-dropdown' : ''}
                  ref={hasSub ? dropdownRef : null}
                >
                  {hasSub ? (
                    <div className="nav__dropdown-wrapper">
                      <button
                        className={`nav__dropdown-toggle ${parentActive ? 'active' : ''}`}
                        onClick={(e) => toggleDropdown(e, item.id)}
                      >
                        {item.title}
                        <MdKeyboardArrowDown className={`dropdown-arrow ${activeDropdown === item.id ? 'active' : ''}`} />
                      </button>
                      {activeDropdown === item.id && (
                        <ul className="nav__submenu">
                          {item.submenu.map(subitem => {
                            const subActive = isLinkActive(subitem.link);
                            return (
                              <li key={subitem.id}>
                                <a
                                  href={subitem.link}
                                  className={subActive ? 'active' : ''}
                                  onClick={(e) => handleNavClick(e, subitem.link)}
                                >
                                  {subitem.title}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.link}
                      className={isLinkActive(item.link) ? 'active' : ''}
                      onClick={(e) => handleNavClick(e, item.link)}
                    >
                      {item.title}
                    </a>
                  )}
                </li>
              );
            })
          }
        </ul>
                
        {isInternshipPage && (
          <div className='nav__apply-btn'>
            <a 
              href="#contact" 
              onClick={scrollToContact}
              className="btn-apply-now"
            >
              Enroll Now
            </a>
          </div>
        )}
        
        {!isInternshipPage && <div className='nav__rightspacer'></div>}
      </div>
    </nav>
  )
}

export default Navbar
